import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import postCoverPreVendas from "@/assets/cover-pre-vendas.jpg";
import postFunil from "@/assets/post-pre-vendas-funil.jpg";
import postCadencia from "@/assets/post-pre-vendas-cadencia.jpg";
import postCoverComoVenderMais from "@/assets/cover-como-vender-mais.jpg";
import postCoverColdcalling from "@/assets/cover-cold-calling.jpg";
import postCoverEstrategias from "@/assets/cover-13-estrategias.jpg";
import {
  ArrowRight,
  Clock,
  User,
  BookOpen,
  CheckCircle,
  ChevronRight,
  Target,
  Zap,
  Users,
  Phone,
  BarChart3,
  Star,
  AlertTriangle,
  Lightbulb,
  Award,
  Filter,
  Calendar,
  TrendingUp,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const PreVendasSDRBDR2026 = () => {
  const navigate = useNavigate();

  const checklist = [
    {
      number: "01",
      title: "Entenda a Diferença Real Entre SDR, BDR e Vendedor Closer",
      icon: Users,
      problem: "Sua equipe ainda mistura prospecção, qualificação e fechamento na mesma pessoa?",
      action: "Separe funções. SDR qualifica leads inbound. BDR prospecta outbound. Closer fecha. Cada papel exige skill, métrica e cadência diferente. Misturar tudo é o motivo pelo qual seu pipeline é caótico.",
      checkItems: [
        "Mapeie quem faz prospecção, quem qualifica e quem fecha hoje",
        "Defina papéis claros com job description por função",
        "Crie metas e métricas separadas (atividade para SDR/BDR, receita para closer)",
        "Estabeleça SLA de passagem de bastão entre SDR/BDR e closer",
      ],
      story: null,
    },
    {
      number: "02",
      title: "Defina o ICP Antes de Contratar o Primeiro SDR",
      icon: Target,
      problem: "Sua equipe está prospectando todo mundo que respira CNPJ?",
      action: "ICP (Ideal Customer Profile) não é um perfil genérico — é um filtro com 5 a 7 critérios objetivos: setor, porte, faturamento, dor crítica, gatilho de compra e poder de decisão. Sem ICP, seu SDR queima 80% do tempo com lead errado.",
      checkItems: [
        "Liste seus 10 melhores clientes e identifique padrões de setor, porte e dor",
        "Defina 3 critérios eliminatórios e 3 critérios desejáveis",
        "Documente o ICP em uma página e cole na parede da pré-vendas",
        "Revise o ICP a cada 90 dias com base no histórico de fechamento",
      ],
      story: null,
    },
    {
      number: "03",
      title: "Construa Listas Segmentadas — Não Compre Bases Frias",
      icon: Filter,
      problem: "Sua pré-vendas trabalha com listas compradas que dão 1% de resposta?",
      action: "Listas geradas internamente convertem 5x mais. Use LinkedIn Sales Navigator, Apollo, Cognism ou pesquisa manual no LinkedIn para montar listas alinhadas ao ICP. Qualidade > quantidade. 100 contatos certos batem 5.000 contatos errados.",
      checkItems: [
        "Defina os filtros do Sales Navigator a partir do ICP",
        "Construa listas semanais de no máximo 200 contatos altamente qualificados",
        "Enriqueça os dados (cargo, empresa, gatilho) antes de abordar",
        "Proíba a compra de bases genéricas — elas envenenam a entregabilidade do e-mail",
      ],
      story: null,
    },
    {
      number: "04",
      title: "Crie uma Cadência de Prospecção Multicanal",
      icon: Calendar,
      problem: "Seu SDR liga uma vez, manda um e-mail e desiste no segundo dia?",
      action: "Cadência ideal em 2026: 12 a 15 toques em 18 a 21 dias, alternando telefone, e-mail, LinkedIn e WhatsApp. Cada toque agrega contexto novo. A maioria dos fechamentos acontece entre o 6º e o 12º toque — quem para antes está doando lead para o concorrente.",
      checkItems: [
        "Documente uma cadência padrão com canal, dia e mensagem de cada toque",
        "Configure a cadência em uma ferramenta (Reev, Meetime, Outreach ou Apollo)",
        "Combine 'toques manuais' (ligação, mensagem personalizada) com 'toques automáticos'",
        "Meça resposta por canal e otimize a cadência mensalmente",
      ],
      story: `Joice Aparecida de Oliveira, da Mapfre Seguros, aprendeu na prática o poder do follow-up estruturado. Antes do treinamento, ela parava no segundo contato. Depois, passou a aplicar cadências que respeitavam o tempo do cliente. "Eu consegui realizar 4 vendas de um produto que ninguém conseguiu vender na empresa, graças as técnicas e estratégias que aprendi no treinamento." — Cadência consistente é o que separa quem fecha de quem desiste.`,
    },
    {
      number: "05",
      title: "Use BANT, CHAMP ou GPCT Para Qualificar Sem Achismo",
      icon: CheckCircle,
      problem: "Seu SDR passa qualquer lead que aceitou conversar para o closer?",
      action: "Qualificação é filtro, não cortesia. Implemente um framework objetivo: BANT (Budget, Authority, Need, Timing), CHAMP (Challenges, Authority, Money, Prioritization) ou GPCT (Goals, Plans, Challenges, Timeline). Sem framework, o pipeline incha de lead que nunca compra.",
      checkItems: [
        "Escolha um framework e treine toda a pré-vendas no mesmo método",
        "Crie um scorecard com pontuação mínima para virar SQL",
        "Documente as perguntas obrigatórias de qualificação",
        "Audite calls de SDR semanalmente e dê feedback baseado no scorecard",
      ],
      story: null,
    },
    {
      number: "06",
      title: "Personalize a Abertura — Adeus aos E-mails Genéricos",
      icon: Lightbulb,
      problem: "Seus e-mails de cold outreach começam com 'Espero que esteja bem'?",
      action: "Em 2026, lead ignora qualquer mensagem que parece template. Use 30 segundos de pesquisa para citar um post recente no LinkedIn, uma notícia da empresa ou uma dor pública do setor. Personalização aumenta resposta em até 4x.",
      checkItems: [
        "Crie um template com 3 campos obrigatórios de personalização",
        "Treine SDRs em pesquisa rápida de 60 segundos antes de cada toque",
        "Proíba abertura com 'Espero que esteja bem' e 'Tudo bem?'",
        "Acompanhe taxa de abertura, resposta e reply positivo por SDR",
      ],
      story: `Gabriele Aparecida da Cruz, da BRINQPET, aprendeu que personalizar muda o jogo. "O curso foi prático, dinâmico e extremamente didático. Hoje vejo o mundo das vendas com outros olhos." — Mensagens personalizadas convertem mais porque respeitam o tempo e a inteligência do prospect.`,
    },
    {
      number: "07",
      title: "Domine o Cold Call Como Habilidade Estratégica",
      icon: Phone,
      problem: "Sua equipe acredita que ligação fria 'não funciona mais'?",
      action: "Cold call não morreu — virou skill avançada. Em 2026, com inboxes saturados, a ligação certeira em 27 segundos é o canal de maior conversão para ICPs C-level. Quem domina o telefone tem vantagem competitiva real.",
      checkItems: [
        "Crie um script de abertura com gancho de 8 segundos",
        "Treine a técnica do PATTI: Pause, Apresente, Tema, Transição, Interesse",
        "Faça role-play de cold call 2x por semana com gravação",
        "Meça connect rate, taxa de conversa e agendamento por SDR",
      ],
      story: null,
    },
    {
      number: "08",
      title: "Implemente um Handoff Estruturado Entre SDR e Closer",
      icon: ArrowRight,
      problem: "O closer recebe a reunião sem contexto e queima o lead?",
      action: "Crie um handoff documentado: o SDR só passa a reunião quando preenche um briefing com dor, contexto, decisor e próximo passo. Closer entra preparado, lead se sente respeitado, taxa de show-up sobe.",
      checkItems: [
        "Crie um template de briefing de handoff (dor, contexto, decisor, timing, BANT)",
        "Defina SLA: closer recebe a reunião com 24h de antecedência",
        "Implemente reunião rápida de alinhamento SDR + closer antes da call",
        "Audite: nenhuma reunião acontece sem briefing completo",
      ],
      story: null,
    },
    {
      number: "09",
      title: "Meça as Métricas Certas — Não Só Reuniões Marcadas",
      icon: BarChart3,
      problem: "Sua única métrica de pré-vendas é 'meetings agendadas'?",
      action: "Meeting marcada é vaidade — meeting realizada e qualificada é receita. Acompanhe show-up rate, taxa de SQL, conversão para oportunidade e receita gerada por SDR. Métrica errada gera comportamento errado.",
      checkItems: [
        "Defina os 5 KPIs principais: atividade, conversa, meeting marcada, meeting realizada, SQL",
        "Crie dashboard visual atualizado diariamente",
        "Faça reunião semanal de pipeline review com métricas de pré-vendas",
        "Bonifique por SQL gerado, não apenas por reunião agendada",
      ],
      story: `Shara Cartaxo, Coordenadora de Vendas na Latina Eletrodomésticos, encontrou no método o que faltava. "Buscava aprimorar minhas habilidades em negociação e encontrei exatamente o que precisava. Hoje fecho negócios com muito mais segurança." — Métricas claras dão confiança para decidir e cobrar.`,
    },
    {
      number: "10",
      title: "Treine Sua Pré-Vendas Toda Semana — Sem Exceção",
      icon: Award,
      problem: "Seu SDR foi treinado no onboarding e nunca mais?",
      action: "Pré-vendas é função de alta rotatividade e alta pressão. Sem treino semanal, a performance despenca em 90 dias. Sessão obrigatória: 30 min/semana com role-play, escuta de calls e estudo de objeções.",
      checkItems: [
        "Bloqueie 30 min toda semana para treinamento de pré-vendas",
        "Grave e analise pelo menos 1 call de cada SDR por semana",
        "Use IA para simular cenários de prospecção e qualificação",
        "Crie biblioteca de melhores calls para onboarding e referência",
      ],
      story: `Giovanna Amaro Gireli, do Citróleo Group, entendeu que treinamento não é evento, é cultura. "O treinamento com o Junior foi uma experiência extremamente valiosa. De forma dinâmica, ele trouxe insights poderosos para o time e também no nível pessoal." — Treinamento contínuo é o que sustenta resultado de longo prazo.`,
    },
    {
      number: "11",
      title: "Use IA Para Multiplicar a Capacidade da Pré-Vendas",
      icon: Zap,
      problem: "Sua equipe ainda perde 2 horas por dia escrevendo e-mails do zero?",
      action: "IA não substitui pré-vendas — multiplica. Use IA para enriquecer leads, gerar primeiras versões de mensagens, resumir calls, sugerir próximos passos e simular role-play. SDR que usa IA bem produz 3x mais.",
      checkItems: [
        "Adote uma ferramenta de IA para enriquecimento e personalização (Apollo, Clay, Cognism)",
        "Use IA para transcrever e resumir calls (Gong, Chorus, Fireflies)",
        "Treine SDRs a usar IA como copiloto, não como autor",
        "Mensure ganho de produtividade (atividades/dia, tempo por contato)",
      ],
      story: `Silvete Araujo, Diretora de Negócios da Zentai Benefícios, levou a equipe para conhecer o que IA pode fazer em vendas. "Uma experiência extremamente enriquecedora, que trouxe novas perspectivas sobre o uso da inteligência artificial em negociações e estratégias comerciais." — IA bem usada eleva o teto de toda a operação comercial.`,
    },
    {
      number: "12",
      title: "Crie um Processo de Reciclagem de Lead — Nada Vai Para o Lixo",
      icon: TrendingUp,
      problem: "Lead 'não agora' some do CRM e nunca mais é trabalhado?",
      action: "Reciclagem é dinheiro escondido. Crie cadência específica para lead 'sem timing': nurturing por e-mail, retomada em 60/90 dias e gatilhos de re-engajamento. Em média, 25% dos 'não agora' viram cliente em 6 meses.",
      checkItems: [
        "Crie status específico 'Reciclagem' no CRM",
        "Implemente cadência de re-engajamento automática a cada 60-90 dias",
        "Defina gatilhos de retomada (mudança de cargo, novo round, anúncio público)",
        "Acompanhe taxa de conversão da base reciclada — você vai se surpreender",
      ],
      story: null,
    },
    {
      number: "13",
      title: "Construa uma Cultura de Feedback e Coaching Diário",
      icon: Star,
      problem: "Seu coordenador de pré-vendas só fala com o SDR no 1:1 mensal?",
      action: "Coaching é diário, não mensal. Implemente ritmo: 15 min de standup diário, escuta de calls semanal, 1:1 quinzenal e revisão de pipeline semanal. Sem ritmo, a pré-vendas vira um time sem técnico.",
      checkItems: [
        "Standup diário de 15 min (atividade do dia, blockers, prioridades)",
        "Escuta de call semanal com feedback estruturado (técnica EBI: estado, base, impacto)",
        "1:1 quinzenal focado em desenvolvimento, não em cobrança",
        "Pipeline review semanal com critério objetivo de avanço de fase",
      ],
      story: `Cibele Maciel, vendedora com mais de 20 anos de experiência, sentiu na pele o impacto do coaching certo. "Junior, seu treinamento quebrou tabus que carrego há mais de 20 anos em vendas! Foi direto, prático e profundo. Já estou aplicando o que aprendi e voando em resultados." — Feedback constante é o que transforma talento em performance.`,
    },
  ];

  const relatedPosts = [
    {
      title: "Como Vender Mais em 2026: 13 Passos Práticos",
      link: "/posts/como-vender-mais-2026",
      image: postCoverComoVenderMais,
      readTime: "20 min",
    },
    {
      title: "Cold Calling em 2026: Está Morto ou Ainda Funciona?",
      link: "/posts/cold-calling-2026",
      image: postCoverColdcalling,
      readTime: "15 min",
    },
    {
      title: "13 Estratégias de Vendas Para Dominar Qualquer Negociação",
      link: "/posts/13-estrategias-vendas-2026",
      image: postCoverEstrategias,
      readTime: "25 min",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Que Gera Pipeline Previsível",
    description:
      "Guia completo para estruturar uma operação de pré-vendas com SDR e BDR em 2026. ICP, cadência multicanal, qualificação BANT/CHAMP, handoff, métricas e IA aplicada à prospecção.",
    author: {
      "@type": "Person",
      name: "Junior Bianchim",
      jobTitle: "Especialista em Treinamento de Equipes Comerciais",
    },
    publisher: { "@type": "Organization", name: "Bianchim Business" },
    datePublished: "2026-03-18",
    dateModified: "2026-03-18",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/pre-vendas-sdr-bdr-2026",
    },
    image: "https://bianchimbusiness.com.br/og-pre-vendas-sdr-bdr-2026.jpg",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Qual a diferença entre SDR e BDR?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "SDR (Sales Development Representative) qualifica leads inbound — quem chegou pelo marketing. BDR (Business Development Representative) faz prospecção outbound ativa — vai atrás do lead. Os dois são pré-vendas, mas com fonte e abordagem diferentes.",
        },
      },
      {
        "@type": "Question",
        name: "Quantos toques uma cadência de prospecção deve ter em 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A cadência ideal tem entre 12 e 15 toques distribuídos em 18 a 21 dias, alternando telefone, e-mail, LinkedIn e WhatsApp. A maioria dos fechamentos acontece entre o 6º e o 12º toque.",
        },
      },
      {
        "@type": "Question",
        name: "Quando contratar o primeiro SDR?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Contrate o primeiro SDR quando: (1) você já tem ICP validado, (2) o closer está gastando mais de 30% do tempo prospectando e (3) existe processo documentado de qualificação. Antes disso, o SDR vai patinar.",
        },
      },
      {
        "@type": "Question",
        name: "Quais métricas acompanhar em pré-vendas?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "As 5 principais são: atividades por dia (ligações, e-mails, conexões), taxa de conversa, reuniões agendadas, reuniões realizadas (show-up rate) e SQLs gerados. Receita gerada por SDR é o KPI final.",
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Pré-Vendas em 2026: Como Estruturar um Time SDR/BDR Previsível"
        description="Guia completo para estruturar pré-vendas em 2026. ICP, cadência multicanal, qualificação BANT/CHAMP, handoff, métricas e IA aplicada à prospecção. 13 passos práticos."
        keywords="pré-vendas, SDR, BDR, prospecção outbound, qualificação de leads, BANT, CHAMP, cadência de prospecção, pipeline previsível, prospecção 2026, Sales Development"
        canonicalUrl="https://bianchimbusiness.com.br/posts/pre-vendas-sdr-bdr-2026"
        ogType="article"
        articlePublishedTime="2026-03-18"
        articleAuthor="Junior Bianchim"
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[50vh] sm:h-[60vh] relative overflow-hidden">
                <img
                  src={postCoverPreVendas}
                  alt="Time de pré-vendas SDR/BDR trabalhando em ambiente corporativo moderno representando estruturação de prospecção em 2026"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-32 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Estrutura Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Pré-Vendas em 2026:{" "}
                    <span className="text-gradient-brand">
                      Como Estruturar um Time SDR/BDR Que Gera Pipeline Previsível
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>22 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      <span>18 Mar 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Introduction */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
                    <p className="text-xl leading-relaxed">
                      <strong className="text-foreground">Por que algumas empresas batem meta todo trimestre e outras vivem de oscilação?</strong> A resposta quase sempre está na pré-vendas. Quem tem SDR e BDR estruturados gera pipeline previsível. Quem não tem, joga no escuro.
                    </p>
                    <p>
                      Em 2026, o mercado B2B mudou: inbox saturado, decisor inacessível, ciclo de venda mais longo. A diferença entre ter pipeline cheio ou vazio está em <strong className="text-foreground">como você estrutura a primeira camada do seu funil — a pré-vendas</strong>.
                    </p>
                    <p>
                      Este guia é o passo a passo que aplico em equipes de empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros, Zentai Benefícios e Triplos Tecnologia. <strong className="text-foreground">São 13 passos para construir, do zero ou da bagunça, uma pré-vendas que gera SQL todo mês — não só meta de meeting.</strong>
                    </p>
                    <Card className="p-6 border-primary/20 bg-primary/5 my-8">
                      <div className="flex items-start gap-4">
                        <AlertTriangle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-bold text-foreground mb-2">Antes de continuar, faça este teste:</p>
                          <p className="text-muted-foreground">
                            Pergunte ao seu time comercial: <strong className="text-foreground">"Quantos SQLs entraram no pipeline na semana passada?"</strong>. Se ninguém souber responder com número, sua pré-vendas precisa ser estruturada antes de qualquer outra coisa.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </section>

            {/* Funil image */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postFunil}
                      alt="Funil de vendas estruturado em quadro branco mostrando etapas de prospecção, qualificação e fechamento em uma operação de pré-vendas moderna"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* The 13 Steps */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                    Os 13 Passos Para Estruturar Pré-Vendas em 2026
                  </h2>
                  <p className="text-muted-foreground mb-10">
                    Cada passo é um diagnóstico + ação + checklist. Aplique em sequência ou ataque o gargalo mais doloroso primeiro.
                  </p>

                  <div className="space-y-12">
                    {checklist.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div key={index} id={`passo-${item.number}`}>
                          <Card className="p-6 sm:p-8 border-border hover:border-primary/30 transition-all">
                            <div className="flex items-center gap-4 mb-6">
                              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <span className="text-primary font-extrabold text-xl">{item.number}</span>
                              </div>
                              <div className="flex-1">
                                <h3 className="text-xl sm:text-2xl font-bold text-foreground">{item.title}</h3>
                              </div>
                              <Icon className="w-6 h-6 text-primary flex-shrink-0 hidden sm:block" />
                            </div>

                            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-4 mb-6">
                              <p className="text-sm font-semibold text-destructive flex items-center gap-2 mb-1">
                                <AlertTriangle className="w-4 h-4" />
                                Diagnóstico
                              </p>
                              <p className="text-muted-foreground">{item.problem}</p>
                            </div>

                            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6">
                              <p className="text-sm font-semibold text-primary flex items-center gap-2 mb-1">
                                <Zap className="w-4 h-4" />
                                Ação Imediata
                              </p>
                              <p className="text-muted-foreground">{item.action}</p>
                            </div>

                            <div className="space-y-3 mb-6">
                              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-primary" />
                                Checklist de Implementação
                              </p>
                              {item.checkItems.map((check, i) => (
                                <div key={i} className="flex items-start gap-3 pl-2">
                                  <div className="w-5 h-5 rounded border-2 border-primary/40 flex-shrink-0 mt-0.5" />
                                  <span className="text-sm text-muted-foreground">{check}</span>
                                </div>
                              ))}
                            </div>

                            {item.story && (
                              <div className="bg-muted/30 rounded-xl p-5 border-l-4 border-primary">
                                <p className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                                  <Star className="w-4 h-4 text-primary" />
                                  Case Real
                                </p>
                                <p className="text-sm text-muted-foreground leading-relaxed italic">
                                  {item.story}
                                </p>
                              </div>
                            )}
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Cadencia image */}
            <section className="py-6">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={postCadencia}
                      alt="SDR fazendo cold call com headset em mesa organizada com CRM aberto e cadência de prospecção planejada"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Summary Checklist */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-foreground">
                    Resumo: O Mapa Completo Para Estruturar a Sua Pré-Vendas
                  </h2>
                  <Card className="p-6 sm:p-8 border-primary/20">
                    <div className="space-y-4">
                      {checklist.map((item, index) => (
                        <a
                          key={index}
                          href={`#passo-${item.number}`}
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary/5 transition-colors group cursor-pointer"
                        >
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-bold text-sm">{item.number}</span>
                          </div>
                          <span className="text-foreground font-medium group-hover:text-primary transition-colors flex-1">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </a>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-foreground">
                    Pipeline Previsível Não é Sorte — É Engenharia Comercial
                  </h2>
                  <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                    <p>
                      A diferença entre uma pré-vendas que funciona e uma que vive em crise não é o tamanho do investimento — é o nível de <strong className="text-foreground">disciplina, processo e treino</strong>.
                    </p>
                    <p>
                      ICP claro. Listas segmentadas. Cadência documentada. Qualificação por framework. Handoff estruturado. Métricas reais. Treino semanal. IA como copiloto. Reciclagem de lead. Coaching diário. <strong className="text-foreground">Quando esses 13 elementos rodam juntos, pipeline deixa de ser sorte e vira engenharia.</strong>
                    </p>
                    <p>
                      O melhor momento para estruturar sua pré-vendas foi há 6 meses. O segundo melhor momento é hoje. O mercado de 2026 vai recompensar quem tem processo e punir quem ainda depende de talento individual.
                    </p>
                    <p>
                      <strong className="text-foreground">Comece pelo passo 01. Implemente um por semana. Em 90 dias, sua pré-vendas vai parecer outra empresa.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Posts */}
            <section className="py-10 sm:py-16 bg-muted/30">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold mb-8 text-foreground">Leitura Recomendada</h2>
                  <div className="grid sm:grid-cols-3 gap-6">
                    {relatedPosts.map((post, index) => (
                      <Link key={index} to={post.link} className="block group">
                        <Card className="overflow-hidden border-border hover:border-primary/30 transition-all h-full">
                          <div className="w-full h-36 overflow-hidden">
                            <img
                              src={post.image}
                              alt={`Capa do artigo ${post.title} - conteúdo complementar sobre vendas e prospecção`}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="p-4">
                            <h3 className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {post.title}
                            </h3>
                            <span className="text-xs text-muted-foreground mt-2 block">{post.readTime} de leitura</span>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Author */}
            <section className="py-10 sm:py-16">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  <Card className="p-6 sm:p-8 border-primary/20 bg-gradient-to-br from-card to-primary/5">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-primary/30">
                        <img
                          src={juniorBianchim}
                          alt="Junior Bianchim, especialista em treinamento de equipes comerciais e estruturação de pré-vendas"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-xl font-bold text-foreground mb-1">Junior Bianchim</h3>
                        <p className="text-primary font-medium text-sm mb-3">Especialista em Treinamento de Equipes Comerciais</p>
                        <p className="text-muted-foreground text-sm">
                          Com mais de 20 anos de experiência em vendas B2B e B2C, Junior já estruturou operações de pré-vendas em empresas como Citróleo Group, Latina Eletrodomésticos, Mapfre Seguros e Zentai Benefícios. Fundador do Vendas Trainer, plataforma de IA para treinamento de SDR, BDR e closers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="py-16 sm:py-20 bg-muted/30">
              <div className="container mx-auto px-4">
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-card via-primary/5 to-accent/10 border-primary/30 text-center max-w-4xl mx-auto">
                  <div className="relative z-10">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4">
                      <span className="text-gradient-brand">Treine Sua Pré-Vendas Com IA</span>
                    </h2>
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                      No Vendas Trainer, sua equipe de SDR e BDR pratica cold call, qualificação e cadência em simulações reais com inteligência artificial.
                      Feedback instantâneo, gamificação e métricas claras.
                      <strong className="text-foreground"> Comece grátis hoje mesmo.</strong>
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        size="lg"
                        onClick={() => navigate("/auth?mode=signup")}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow text-lg px-8 py-6 font-semibold"
                      >
                        Criar Conta Grátis
                        <ArrowRight className="ml-2 w-5 h-5" />
                      </Button>
                      <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/50 hover:bg-primary/10" asChild>
                        <a href="https://wa.me/5516981960018" target="_blank" rel="noopener noreferrer">
                          Agendar Demonstração
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </section>
          </article>

          <PageNavigation />
        </main>

        <Footer />
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
    </>
  );
};

export default PreVendasSDRBDR2026;
